import React from "react";
import ChatMessageList from "./ChatMessageList.component";
import ChatComposer, { type ComposerFile } from "./ChatComposer.component";
import type { ChatMessage } from "./chatMessages";
import styles from "./AiChat.module.scss";

/** Props for the chat surface, produced by `useAiChat`. Defined here so the
 *  presentational surface stays decoupled from the hook and can be driven by
 *  stubbed state in tests. */
export interface AiChatProps {
  messages: ChatMessage[];
  note: string;
  files: ComposerFile[];
  /** True while a draft is generating — composer is disabled. */
  busy: boolean;
  canSend: boolean;
  onNoteChange: (value: string) => void;
  onFilesPicked: (files: File[]) => void;
  onRemoveFile: (id: string) => void;
  onSend: () => void;
  onRetry: () => void;
}

/** The conversational draft surface: a scrolling message thread over a pinned
 *  composer. Purely presentational — all state comes from props. */
const AiChat: React.FC<AiChatProps> = ({
  messages,
  note,
  files,
  busy,
  canSend,
  onNoteChange,
  onFilesPicked,
  onRemoveFile,
  onSend,
  onRetry,
}) => (
  <div className={styles.surface}>
    <ChatMessageList messages={messages} onRetry={onRetry} />
    <ChatComposer
      note={note}
      files={files}
      disabled={busy}
      canSend={canSend}
      onNoteChange={onNoteChange}
      onFilesPicked={onFilesPicked}
      onRemoveFile={onRemoveFile}
      onSend={onSend}
    />
  </div>
);

export default React.memo(AiChat);
